import { createSelector } from '@reduxjs/toolkit';

import { SITE_EXTRACTION_TABLE_HEADERS } from '../../../../modules/commentary/consts.ts';
import {
  selectAppliedSiteAdjsDisplay,
  selectSiteAdjsDisplay,
} from '../adjustmentSelectors/selectSiteAdj.ts';
import { selectCalculatedMarketAdjustmentsDisplay } from '../calculatedMarketAdjustmentSelectors/selectCalculatedMarketAdjustment.ts';
import { selectSiteValueCommentary } from '../opinionOfSiteValueSelectors.ts';
import { selectAdjustmentSettings, selectCompSequence, selectCompTypes } from '../orderSelectors.ts';
import { selectManualAdjustmentsEntities } from '../propertyDataSelectors/manualAdjustmentsSelectors.ts';
import { selectSiteDisplay, selectSitesDisplay } from '../propertyDataSelectors/siteSelectors.ts';
import { selectSalePricesDisplay } from '../propertyDataSelectors/transactionSelectors.ts';
import { selectImprovementsCostsDisplay } from '../selectImprovementsCost.ts';
import { selectSiteCostPerSFsDisplay } from '../selectSiteCostPerSf.ts';
import { selectSiteValuesDisplay } from '../selectSiteValue.ts';
import { selectSiteValueRatiosDisplay } from '../selectSiteValueRatio.ts';
import { getCommentaryTableHelperText } from '../utils/getCommentaryTableHelperText.ts';
import { isAivRow, isArvRow } from './utils.ts';

const getSiteInfoDisplay = (siteInfo: ReturnType<typeof selectSiteDisplay> | undefined) =>
  `${siteInfo?.area?.toString() ?? '-'} | ${siteInfo?.location ?? '-'} | ${siteInfo?.view ?? '-'}`;

const selectSiteExtractionCommentaryData = createSelector(
  [
    selectCompSequence,
    selectSitesDisplay,
    selectSalePricesDisplay,
    selectCalculatedMarketAdjustmentsDisplay,
    selectImprovementsCostsDisplay,
    selectSiteValuesDisplay,
    selectSiteValueRatiosDisplay,
    selectSiteCostPerSFsDisplay,
    selectSiteAdjsDisplay,
    selectAppliedSiteAdjsDisplay,
    selectAdjustmentSettings,
    selectManualAdjustmentsEntities,
  ],
  (
    compSequence,
    sitesDisplay,
    salePricesDisplay,
    marketAdjustmentsDisplay,
    improvementsCostsDisplay,
    siteValuesDisplay,
    siteValueRatiosDisplay,
    siteCostPerSFsDisplay,
    siteAdjsDisplay,
    appliedSiteAdjsDisplay,
    adjustmentSettings,
    manualAdjustmentsEntities,
  ) => ({
    helperText: getCommentaryTableHelperText(
      adjustmentSettings,
      'site',
      undefined,
      manualAdjustmentsEntities,
    ),
    data: ['subject', ...compSequence].map((id) => ({
      label: id === 'subject' ? 'SUB' : `CP ${compSequence.indexOf(id) + 1}`,
      siteInfo: getSiteInfoDisplay(sitesDisplay[id]),
      price: salePricesDisplay[id],
      marketAdjustment: marketAdjustmentsDisplay[id],
      depCost: improvementsCostsDisplay?.[id],
      siteValue: siteValuesDisplay?.[id],
      siteValueRatio: siteValueRatiosDisplay?.[id],
      siteCostPerSF: siteCostPerSFsDisplay?.[id],
      siteAdjustment: siteAdjsDisplay?.[id],
      appliedAdjustment: appliedSiteAdjsDisplay?.[id]?.value,
      id,
    })),
  }),
);

export const selectSiteExtractionCommentaryTableDisplay = createSelector(
  [selectSiteExtractionCommentaryData, selectSiteValueCommentary, selectCompTypes],
  (
    { helperText, data },
    siteValueCommentary,
    compTypes,
  ): { data: [string[], ...string[][]]; helperText?: string; commentary: string } => ({
    data: [
      SITE_EXTRACTION_TABLE_HEADERS,
      ...data.map((row) => [
        row.label,
        row.siteInfo ?? '-',
        row.price ?? '-',
        row.marketAdjustment ?? '-',
        row.depCost ?? '-',
        row.siteValue ?? '-',
        row.siteValueRatio ?? '-',
        row.siteCostPerSF ?? '-',
        row.siteAdjustment ?? '-',
        row.appliedAdjustment ?? '-',
      ]),
    ],
    commentary: siteValueCommentary,
    helperText,
  }),
);
